import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  BellOff,
  Phone,
  Mail,
  MailOpen,
  MessageSquare,
  MessageSquareMore,
  PhoneOutgoing,
  CheckCircle2,
  CalendarCheck,
  Lock,
  Play,
  PhoneIncoming,
  Handshake,
  RefreshCw,
  ScrollText,
  Send,
  UserCog,
  UserRoundCheck,
  Voicemail,
  History,
  Zap,
  Trash2,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Headset,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

import {
  ensureAuthorized,
  getAccessToken,
  openAuthorizePopup,
} from '../../../lib/ringcentral/auth';
import JourneyTimeline from './potentialClients/JourneyTimeline';
import {
  AGE_PILL,
  checkpointLine,
  needOf,
} from './potentialClients/signingUpUtils';
import LeadWorkNotesPad, {
  useLeadWorkNotesDraft,
} from '../leads/LeadWorkNotesPad';
import {
  useSaveLeadWorkNotes,
  useFetchRunRcActivity,
  useAdoptOutbound,
  useFetchCallProfile,
  useSaveLeadEmail,
} from '../../../services/onboardingBoard';
import {
  fullLeadName,
  renderScript,
  resolveScriptVariant,
  scriptForStep,
  UNRESOLVED_TOKEN_RE,
  stepsForToday,
  chainBlockerOf,
} from './boardUtils';
import {
  windowLabel,
  stepCountdownLabel,
  stepTone,
  stepIsClosed,
  inPushWindow,
  effectiveStepWindow,
  stepPausedKindNow,
  shortDuration,
  intakeStartOf,
  intakeReleaseOf,
  intakeNotStarted,
  startsAtText,
  AWAITING_LOG_TTL_MS,
} from './slaUtils';
import { AwayGlyph, useWorkCalendar } from './LeadTile';
import PickerMenu from './PickerMenu';
import { useCallCenter } from './callCenter/CallCenterProvider';
import { LeadDetailSections, formatPhone } from './LeadDetailCards';
import LeadTranscriptsCard from './LeadTranscriptsCard';
import { isOwnedBy, NotYoursNote } from './ownership';

// Manager-only quiet card at the TOP of the panel: move this lead/run to a
// different Support Specialist's board (reassign targets mirror the
// scheduling rule — only they can own leads; the server enforces it too).
// Select-and-go, no confirm — reassignment is reversible the same way.
const ReassignCard = ({
  currentOwnerId,
  currentOwnerName,
  specialists,
  busy,
  onReassign,
}) => (
  <div className="mb-4 rounded-xl bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
      <UserCog size={13} />
      {currentOwnerName
        ? `On ${currentOwnerName}'s board`
        : 'In the shared intake'}
    </div>
    <PickerMenu
      label="Reassign to…"
      disabled={busy}
      onPick={onReassign}
      emptyLabel="No other Sales Reps yet"
      triggerClassName="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-300 dark:border-white/10 dark:text-slate-300 dark:hover:border-white/20"
      options={specialists
        .filter((w) => w.userId !== currentOwnerId)
        .map((w) => ({ value: w.userId, label: w.name }))}
    />
  </div>
);

// Top-level section break: a centered label with a rule running out to both
// edges. The panel is a long scroll of stacked cards, and a plain left-aligned
// label read as just another sub-heading — this gives the majors (Next move,
// My notes, Lead details) a clear horizon line. Sub-labels inside a section
// deliberately stay small and left-aligned, so the hierarchy is obvious.
// Collapsible (Nic 2026-09-30, Signing Up): the title itself toggles, with a
// chevron beside it; `defaultOpen` false for the reference history at the
// bottom.
const Section = ({ title, children, collapsible = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const shown = !collapsible || open;
  return (
    <div className="mb-5">
      {title ? (
        <div className={`flex items-center gap-3 ${shown ? 'mb-3' : ''}`}>
          <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
          {collapsible ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="flex shrink-0 items-center gap-1 rounded-md px-1 text-xs font-semibold uppercase tracking-wider text-slate-400 outline-none hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:text-slate-500 dark:hover:text-slate-300"
            >
              {title}
              <ChevronDown
                size={13}
                className={`transition-transform ${open ? '' : '-rotate-90'}`}
              />
            </button>
          ) : (
            <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              {title}
            </span>
          )}
          <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
        </div>
      ) : null}
      {shown ? children : null}
    </div>
  );
};

// Existing account mark (Nic 2026-09-30): the old amber banner became a
// small icon after the name; the facts show in a hover card (and on focus,
// for keyboards). Visibility only, same as the banner was: a phone match can
// be a relative sharing a number, so a human reviews and Nic links them.
const EXISTING_CARD_W = 260;
const ExistingAccountMark = ({ account }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const show = () => {
    const b = ref.current?.getBoundingClientRect();
    if (!b) return;
    const vw = document.documentElement.clientWidth;
    const w = Math.min(EXISTING_CARD_W, vw - 32);
    setPos({
      top: b.bottom + 8,
      left: Math.min(Math.max(16, b.left - 12), vw - w - 16),
      width: w,
    });
  };
  const hide = () => setPos(null);
  const created = account.accountCreatedAt
    ? new Date(account.accountCreatedAt).toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;
  return (
    <>
      <span
        ref={ref}
        tabIndex={0}
        role="img"
        aria-label={`Existing account: ${account.name}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="inline-flex h-5 w-5 shrink-0 cursor-default items-center justify-center rounded-full bg-amber-100 text-amber-600 outline-none ring-1 ring-amber-200/80 focus-visible:ring-2 focus-visible:ring-amber-400 dark:!bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30"
      >
        <UserRoundCheck size={11} strokeWidth={2.6} />
      </span>
      {pos
        ? createPortal(
            <div
              style={{ position: 'fixed', ...pos }}
              className="pointer-events-none z-[1070] rounded-xl bg-white p-3 text-xs shadow-[0_4px_12px_rgba(15,23,42,0.08),0_16px_40px_rgba(15,23,42,0.14)] ring-1 ring-slate-200/70 dark:!bg-surface-elevated dark:ring-white/10"
            >
              <div className="mb-0.5 flex items-center gap-1.5 font-semibold text-slate-800 dark:text-slate-100">
                <UserRoundCheck
                  size={13}
                  className="shrink-0 text-amber-500 dark:text-amber-300"
                />
                Existing account
              </div>
              <div className="font-medium text-slate-700 dark:text-slate-200">
                {account.name} (
                {account.userType === 'pending_client'
                  ? 'pending client'
                  : 'client'}
                )
              </div>
              <div className="mt-1 leading-relaxed text-slate-500 dark:text-slate-400">
                {created ? `Created ${created}, ` : ''}matched by{' '}
                {account.matchedBy}. They likely signed up on their own. Ping
                Nic to review and link.
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

// What's needed (Nic 2026-09-30): the one thing that must happen next on
// Signing Up, stated plainly (the words come from needOf, shared with the
// call workspace). A meeting still ahead IS the next thing, so it shows
// alone: when, what and with whom. Otherwise the checkpoint's need and how
// long is left, plus how the last meeting went when there was one.
const whenLong = (at) =>
  new Date(at).toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
const NEED_PILL = {
  ...AGE_PILL,
  meeting: 'bg-teal-50 text-teal-700 dark:!bg-teal-500/15 dark:text-teal-300',
};
const NeedStatement = ({ run, now }) => {
  // Weekend wording (2026-11-01): on a closed day "today" reads as the real day.
  const workCalendar = useWorkCalendar();
  const closedUntil = workCalendar && workCalendar.isWorkingToday === false ? workCalendar.nextOpenAt : null;
  const n = needOf(run, now, closedUntil);
  return (
    <div className="rounded-xl bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {n.station}
        </span>
        <span
          title={checkpointLine(run, closedUntil)}
          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${NEED_PILL[n.tone]}`}
        >
          {n.pill}
        </span>
      </div>
      {n.held ? (
        <div className="flex items-start gap-2">
          <CalendarClock
            size={16}
            className="mt-0.5 shrink-0 text-teal-600 dark:text-teal-300"
          />
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-slate-800 dark:text-slate-100">
              Meeting {whenLong(n.meeting.at)}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {n.meeting.what}
              {n.meeting.host ? ` with ${n.meeting.host}` : ''}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="text-[15px] font-semibold text-slate-800 dark:text-slate-100">
            {n.need}
          </div>
          <div
            className={`text-xs ${
              n.overdue
                ? 'font-medium text-red-600 dark:text-red-300'
                : 'text-slate-500 dark:text-slate-400'
            }`}
          >
            {n.due}
          </div>
          {n.prev ? (
            <div className="mt-2 flex items-center gap-1.5 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-white/10 dark:text-slate-400">
              <CalendarClock size={13} className="shrink-0 text-slate-400" />
              {n.prev.outcome === 'missed' ? 'They no-showed' : 'Met'}
              {n.prev.hostName
                ? ` with ${n.prev.hostName.split(/\s+/)[0]}`
                : ''}{' '}
              {whenLong(n.prev.at)}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

// The client's OWN inbound touches — a durable, read-only record ("Glen
// reached out"), lead-named so it can't be misread as a log of what WE did.
// Sits above the action row: "they've called twice" is context you want
// while deciding the next move. The amber detection banner stays the
// actionable prompt; this card survives every dismiss.
const ReachOutsCard = ({ lead, reachOuts }) => {
  // The row whose text is being read (null = closed). Replies land on the
  // SENDER's RC line, which may not be the board owner's (Nic 2026-08-24:
  // Jorge texts from Kit's board and Daniel's reply is invisible to Kit) —
  // so the message itself must be one click away right here.
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (!viewing) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        // Capture phase + stop: Escape closes ONLY this popup — without
        // this the slide-over's own bubble-phase Escape listener also
        // fires and unmounts the whole panel (QA round 3, 2026-08-24).
        e.stopPropagation();
        setViewing(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [viewing]);

  if (!reachOuts?.length) return null;

  const when = (at) =>
    new Date(at).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

  return (
    <div className="mb-3 rounded-xl border border-emerald-100 bg-emerald-50/30 px-3 py-2.5 dark:border-emerald-500/25 dark:bg-emerald-500/10">
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
        <Handshake size={15} />
        {lead.leadFirstName} reached out
      </div>
      <div className="space-y-1">
        {reachOuts.map((r, i) => (
          <div
            key={`${r.at}-${i}`}
            className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
          >
            {r.type === 'call' ? (
              <Phone size={12} className="shrink-0 text-slate-400" />
            ) : (
              <MessageSquare size={12} className="shrink-0 text-slate-400" />
            )}
            <span className="font-medium">
              {r.type === 'call' ? 'Called' : 'Texted'}
            </span>
            <span className="text-slate-400">{when(r.at)}</span>
            {r.type !== 'call' && r.message ? (
              <button
                type="button"
                onClick={() => setViewing(r)}
                title="View message"
                className="ml-auto rounded-md p-1 text-slate-400 transition-colors hover:bg-emerald-100/70 hover:text-emerald-600 dark:hover:bg-emerald-500/15 dark:hover:text-emerald-400"
              >
                <MessageSquareMore size={13} />
              </button>
            ) : null}
          </div>
        ))}
      </div>
      {viewing
        ? createPortal(
            <div className="fixed inset-0 z-[1080] flex items-end justify-center sm:items-center">
              <div
                className="absolute inset-0 bg-slate-900/30 dark:bg-black/60"
                aria-hidden
                onClick={() => setViewing(null)}
              />
              <div className="relative m-4 w-full max-w-md rounded-2xl bg-white p-4 shadow-xl dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
                    <MessageSquare size={15} className="text-emerald-500" />
                    Text from {lead.leadFirstName}
                    <span className="text-xs font-normal text-slate-400">
                      {when(viewing.at)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setViewing(null)}
                    className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="whitespace-pre-wrap rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700 dark:bg-white/5 dark:text-slate-200">
                  {viewing.message}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

// Tiny copy affordance for phone/email values: quiet until hovered, flips to
// a check for a beat after copying.
// The working notepad: one free-text pad per lead, saved EXPLICITLY (no
// autosave). Thin wrapper over the shared LeadWorkNotesPad: this surface owns
// the transport (board endpoint + board query key) and the chrome (`Section`),
// while the pad owns the field, the dirty/Save contract, the grow-only sizing
// and the adoption guard that keeps a background refetch off a dirty pad.
// See src/components/operations/leads/LeadWorkNotesPad.js.
// Not started yet (Nic 2026-10-25): the run's lock, in place of Contact made.
// After hours a greyed "Starts Monday at 8:00 AM" (prod's Intake wording);
// while the board is open, Start now begins its 10-day clock this minute.
const NotStartedCard = ({ startsAt, now, canStartNow, busy, onStartNow }) => (
  <div>
    {canStartNow ? (
      <button
        type="button"
        disabled={busy}
        onClick={onStartNow}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:!bg-slate-200 dark:!text-slate-900 dark:hover:!bg-slate-100"
      >
        <Play size={14} /> Start now
      </button>
    ) : (
      <button
        type="button"
        disabled
        title={`This lead can't be started until ${startsAtText(startsAt, now)}`}
        className="flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-slate-100 px-3 py-2.5 text-sm font-semibold text-slate-400 dark:bg-white/10 dark:text-slate-500"
      >
        <Lock size={14} /> Starts {startsAtText(startsAt, now)}
      </button>
    )}
    <p className="mt-2 text-center text-xs text-slate-400 dark:text-slate-500">
      {canStartNow ? (
        <>
          Its 10-day clock starts {startsAtText(startsAt, now)}. Start now to
          begin it this minute.
        </>
      ) : (
        <>
          This lead arrived outside working hours, so its 10-day clock starts{' '}
          {startsAtText(startsAt, now)}. Nothing is counted against anyone
          until then.
        </>
      )}
    </p>
  </div>
);

const Notepad = ({ lead, user, readOnly = false, collapsible = false }) => {
  const queryClient = useQueryClient();
  const saveNotes = useSaveLeadWorkNotes();
  const notes = useLeadWorkNotesDraft(lead.leadWorkNotes, lead.leadId);

  const persist = async (text) => {
    const res = await saveNotes.mutateAsync({
      leadId: lead.leadId,
      notes: text,
    });
    queryClient.invalidateQueries(['onboarding-board']);
    return res;
  };

  return (
    <Section title="My notes" collapsible={collapsible}>
      <LeadWorkNotesPad
        variant="card"
        readOnly={readOnly}
        value={notes.value}
        onChange={notes.setValue}
        dirty={notes.dirty}
        updatedAt={lead.leadWorkNotesUpdatedAt}
        updatedByLabel={
          lead.leadWorkNotesUpdatedBy === user?.userId ? 'you' : null
        }
        onSave={() =>
          notes.save(persist).catch((e) => {
            toast.error(
              e?.response?.data?.msg || e?.message || 'Could not save notes.',
            );
            // Rethrow so the pad stays dirty and the text is never lost.
            throw e;
          })
        }
      />
    </Section>
  );
};

const chipLabel = (s) => {
  if (s.status === 'skipped_condition') return 'skipped';
  if (s.status === 'skipped_optout') return 'opted out';
  if (s.status === 'skipped_no_email') return 'no email';
  return s.status;
};

const timeLabel = (d) =>
  new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

// Premium hover marker: a quiet icon whose tooltip carries the SLA story.
// The tooltip portals to <body> with fixed positioning — rendered inline it
// gets clipped by the slide-over's scroll container. The x-coordinate clamps
// to the viewport so a marker near the screen edge never spills the bubble
// off-screen.
// tone: 'late' = red (a breach), 'early' = blue (jumped the window), default
// = neutral slate (informational, e.g. the re-timed-window note).
const SlaHint = ({ message, tone }) => {
  const iconRef = useRef(null);
  const [pos, setPos] = useState(null);
  const show = () => {
    const r = iconRef.current?.getBoundingClientRect();
    if (!r) return;
    const half = 124; // w-60 is 240px wide; keep a few px of breathing room
    const x = Math.min(
      Math.max(r.left + r.width / 2, half),
      window.innerWidth - half,
    );
    setPos({ x, y: r.top - 6 });
  };
  return (
    <span
      ref={iconRef}
      className="inline-flex align-middle"
      onMouseEnter={show}
      onMouseLeave={() => setPos(null)}
    >
      <History
        size={12}
        className={
          tone === 'late'
            ? 'text-red-500 dark:text-red-400'
            : tone === 'early'
              ? 'text-blue-500 dark:text-blue-400'
              : 'text-slate-300 dark:text-slate-600'
        }
      />
      {pos
        ? createPortal(
            <span
              style={{ left: pos.x, top: pos.y }}
              className="pointer-events-none fixed z-[1090] w-60 -translate-x-1/2 -translate-y-full rounded-md bg-slate-800 px-2.5 py-1.5 text-left text-[11px] font-medium leading-snug text-white shadow-lg"
            >
              {message}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
};

// On a pending re-timed step: ONE neutral note answering "why did the time
// change?" — never a judgment. Drift vs the original day plan is scorecard
// material (meta stamps feed the metrics page), not a working instruction:
// every marker a worker sees must answer "is this mine to fix?", and drift
// isn't (Nic 2026-08-08).
const ReflowHint = ({ label }) => (
  <SlaHint message={`Re-timed off ${label}. This is normal behavior.`} />
);

// On a done step: a marker exists ONLY when the worker's execution itself
// was off (late or early), judged against the LIVE window — the fair one.
// On time renders green and SILENT: no icon, no drift talk. Behind-the-plan
// drift still stamps into meta (behindScheduleMinutes) and feeds the
// leadership scorecards; it is deliberately invisible on the checklist
// because a worker who hit the window did nothing wrong and can fix nothing
// (Nic 2026-08-08 — yellow on on-time work reads as "did I mess up?" and
// teaches people to ignore yellow).
const preciseDuration = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const mm = Math.floor(s / 60);
  if (mm >= 60) return shortDuration(ms);
  const ss = s % 60;
  return ss ? `${mm}m ${ss}s` : `${mm}m`;
};

// Grow a textarea to its content (used as a callback ref AND in onChange so
// the prefilled script is fully visible the moment compose opens).
const autosizeTextarea = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight + 2}px`;
};

const doneStory = (step) => {
  const m = step.meta || {};
  // The verb matches the action; the row already shows the done time and
  // the window, so the tooltip states only the verdict + the delta.
  const verb = step.type === 'call' ? 'Started' : 'Sent';

  if (m.late || m.completedAfterMissed) {
    const lateMs =
      step.completedAt && step.windowEndAt
        ? new Date(step.completedAt) - new Date(step.windowEndAt)
        : 0;
    const lateBy = lateMs > 0 ? preciseDuration(lateMs) : null;
    // completedAfterMissed stays a meta-only fact (scorecard forensics) —
    // the hover just says late (Nic 2026-08-09: the "was marked missed
    // first" parenthetical read as noise).
    return {
      message: `${verb} ${lateBy ? `${lateBy} after SLA` : 'after SLA'}.`,
      tone: 'late',
    };
  }
  // EARLY is a flag too, not a virtue: spaced touches are the cadence's whole
  // point, so doing a step before its window opened gets called out — in
  // blue, not red: it's a spacing correction, not a breach.
  if (m.early) {
    const earlyMs =
      step.completedAt && step.windowStartAt
        ? new Date(step.windowStartAt) - new Date(step.completedAt)
        : 0;
    const ahead = earlyMs > 0 ? preciseDuration(earlyMs) : null;
    return {
      message: `${verb} ${ahead ? `${ahead} early` : 'before the window opened'}.`,
      tone: 'early',
    };
  }
  // On time: green check only, full stop.
  return null;
};

// One checklist row: the step, its window + live countdown, the script
// inline (tokens rendered so it can be read verbatim), and the action.
// Chained steps (Call #2 is 20-30 min AFTER Call #1 happens) lock until
// their dependency is done; done steps show when they ACTUALLY happened
// next to when they should have.
const StepRow = ({
  step,
  run,
  now,
  cadenceVersion,
  scripts,
  ownerLunch,
  user,
  onCall,
  onManualComplete,
  onSendSms,
  onSendEmail,
  onOpenBio,
  busy,
  isCurrent = false,
  readOnly = false, // not the viewer's lead: show the step, offer no action
}) => {
  const script = scriptForStep(step, scripts);
  // Partner versions: this lead's partner may have its own wording. Resolved
  // BEFORE rendering so tokens fill inside the variant body too. This is the
  // pick that matters — the compose prefill below feeds the send, and the
  // server honours the text the browser posts.
  const content = resolveScriptVariant(script, run.lead);
  // Previews render as the VIEWER — they are the sender when they click
  // Send (personal-numbers round: their name, bio, and own line).
  const rendered = script
    ? renderScript(content.body, {
        lead: run.lead,
        user,
        assigneeNumber: run.ownerPhone,
      })
    : null;
  // DERIVED LUNCH PAUSE (2026-08-11): the window renders as authored; the
  // clock (tone + countdown) runs against the effective window.
  const lunchApplies =
    step.status === 'pending' &&
    !(step.meta?.autoSend && !step.meta?.autoSendBlocked);
  const eff = lunchApplies
    ? effectiveStepWindow(step, ownerLunch, now)
    : { credit: 0 };
  // 'lunch' | 'meeting' | null (Nic 2026-09-25: meetings pause too).
  const lunchPaused = stepPausedKindNow(step, ownerLunch, now);
  // Live credit while pending; the completion stamp preserves it on done
  // steps forever (the audit answer "was this late, or lunch?").
  const lunchCreditMin =
    step.status === 'done'
      ? Number(step.meta?.lunchCreditMinutes || 0)
      : Math.floor((eff.credit || 0) / 60000);
  const clockStep =
    eff.credit > 0
      ? { ...step, windowStartAt: eff.startAt, windowEndAt: eff.endAt }
      : step;
  const tone = stepTone(clockStep, now);
  // A step stays workable (breached or not) until its own working day closes
  // plus the one-hour grace — after that the SERVER 409s any action on it
  // (guardStepActionable/workableUntil). The panel must mirror that or it
  // offers Call/Compose/Mark-done buttons the server refuses, and its
  // countdown keeps ticking "over" all weekend. The tiles already respect
  // this via the same helper; the panel did not.
  const workCalendar = useWorkCalendar();
  const closed = stepIsClosed(step, workCalendar, now);
  const actionable =
    (step.status === 'pending' || step.status === 'missed') && !closed;
  const [showScript, setShowScript] = useState(false);
  // Real-send compose (Phase 3): prefilled with the rendered script, editable
  // before sending. The preview is client-rendered; the server re-renders
  // authoritatively and re-applies the STOP line on texts.
  const [composeOpen, setComposeOpen] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [draftSubject, setDraftSubject] = useState('');
  const [sending, setSending] = useState(false);
  // An unresolved token in the draft (an unfilled [bio], most likely) blocks
  // the send UP FRONT — the server refuses it anyway, but the compose must
  // say so before the click, not after (Nic 2026-08-09).
  const unresolvedToken = UNRESOLVED_TOKEN_RE.test(
    `${draftSubject}\n${draftText}`,
  );
  // Name the token that is actually blocking, so the fix is obvious. [bio] is
  // the one a specialist self-serves, so it wins when several are present.
  const unresolvedHint = /\[bio\]/i.test(`${draftSubject}\n${draftText}`)
    ? null
    : /\[referral source\]/i.test(`${draftSubject}\n${draftText}`)
      ? 'We do not know who referred this lead. Type the source in, or remove the token.'
      : /\[assignee number\]/i.test(`${draftSubject}\n${draftText}`)
        ? 'The lead owner has no number on file. Remove the token, or add their number.'
        : null;

  // Live substitution: the draft prefills ONCE, so a bio saved while the
  // compose is open (the "Add my bio" flow) must flow into it in real time —
  // not only after closing and reopening (Nic 2026-08-09).
  const savedBio = user?.userCadenceBio || null;
  useEffect(() => {
    if (!composeOpen || !savedBio) return;
    setDraftText((t) => t.replace(/\[bio\]/gi, savedBio));
    setDraftSubject((s) => s.replace(/\[bio\]/gi, savedBio));
  }, [composeOpen, savedBio]);

  const openCompose = () => {
    // The STOP line lives outside the editable text (the server appends it
    // on send) — strip a trailing one from the script prefill so it shows
    // in the fixed row below the box instead of inviting edits.
    const prefill =
      step.type === 'sms'
        ? (rendered || '').replace(/\s*Reply STOP to opt-out\.\s*$/i, '')
        : rendered || '';
    setDraftText(prefill);
    setDraftSubject(
      content?.subject
        ? renderScript(content.subject, {
            lead: run.lead,
            user,
            assigneeNumber: run.ownerPhone,
          })
        : '',
    );
    setShowScript(false);
    setComposeOpen(true);
  };

  const doSend = async () => {
    setSending(true);
    try {
      if (step.type === 'sms') {
        // Texts go out AS the signed-in person from their own line, riding
        // the RC login the dialer already uses (identity model 2026-08-10).
        // Signed out → open the RC sign-in POPUP in place (the draft
        // survives; no navigation); toast only when the popup was blocked
        // or abandoned.
        let authorized = await ensureAuthorized();
        if (!authorized) {
          authorized = await openAuthorizePopup();
        }
        if (!authorized) {
          toast.error(
            'Log into RingCentral to send texts. Use the RingCentral chip in the board header to sign in.',
          );
          return;
        }
        await onSendSms(step, {
          text: draftText,
          rcAccessToken: getAccessToken(),
        });
        toast.success('Text sent.');
      } else {
        await onSendEmail(step, { subject: draftSubject, text: draftText });
        toast.success('Email sent.');
      }
      setComposeOpen(false);
    } catch (err) {
      /* surfaced by the mutation's error toast */
    } finally {
      setSending(false);
    }
  };

  // Chain lock: block while an upstream chain step is still owed (skipped
  // dependencies looked through). Gate-only steps (Call #3) stay locked in
  // order but keep their hard window.
  const dependency = actionable
    ? chainBlockerOf(step, run, cadenceVersion)
    : null;
  const chainBlocked = !!dependency;

  // Script toggles are EMAIL-ONLY now (Nic 2026-08-12): call steps carry
  // their VM script inside the Call Command Center's script wing, and the
  // SMS compose panel prefills the script anyway — the checklist toggle was
  // redundant noise for both.
  const scriptLabel = 'Script';
  const scriptToggle =
    rendered && step.type === 'email' ? (
      <button
        type="button"
        onClick={() => setShowScript((v) => !v)}
        title={
          showScript
            ? `Hide the ${scriptLabel.toLowerCase()}`
            : `Show ${scriptLabel.toLowerCase()}: ${script.label}`
        }
        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${
          showScript
            ? 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400'
            : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10'
        }`}
      >
        <ScrollText size={12} /> {scriptLabel}
      </button>
    ) : null;

  const TypeIcon =
    step.type === 'call' ? Phone : step.type === 'sms' ? MessageSquare : Mail;

  // Prefix match mirrors the server's verdict bucketing (any skipped_*).
  const skipped = String(step.status).startsWith('skipped');

  return (
    <div
      className={`rounded-lg p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] ${
        // A skipped step is NOT work — the whole card recedes so the eye
        // never weighs it against the real checklist.
        // The dark hairline only renders when this ISN'T the current step:
        // dark:ring-1/white-10 outranks the plain ring-2 tone classes and
        // was squashing the urgency outline to an invisible sliver.
        skipped
          ? 'bg-slate-50 opacity-60 dark:bg-white/5'
          : `bg-white dark:!bg-surface-card ${
              isCurrent ? '' : 'dark:ring-1 dark:ring-white/10'
            }`
      } ${
        // "You are here": a quiet STATIC ring on the current step, tinted by
        // its urgency. Deliberately no animation — the outline locates, the
        // schedule line escalates.
        isCurrent
          ? tone.state === 'automated'
            ? 'ring-2 ring-violet-400'
            : tone.state === 'due'
              ? 'ring-2 ring-amber-400'
              : tone.state === 'overdue' || step.status === 'missed'
                ? 'ring-2 ring-red-400'
                : 'ring-2 ring-blue-400'
          : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <TypeIcon
            size={14}
            className="shrink-0 text-slate-400 dark:text-slate-500"
          />
          <span className="truncate text-sm font-semibold text-slate-700 dark:text-slate-200">
            {step.label || step.stepKey}
          </span>
          {/* Automated (meta.autoSend): the sweep sends it when the window
              opens — the label says so even after it fired. A blocked one
              keeps the label; the warning line below explains. */}
          {step.meta?.autoSend ? (
            <span
              title="Sends automatically when its window opens. No one's SLA rides on it."
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-50 px-1.5 py-px text-[10px] font-semibold text-violet-600 dark:bg-violet-500/15 dark:text-violet-400"
            >
              <Zap size={10} />
              Automated
            </span>
          ) : null}
        </div>
        {/* ZONE 1 state slot — populated only when the state is EXCEPTIONAL:
            check for done, red pill for missed, muted pill for skipped. A
            live pending step shows nothing here (the schedule countdown IS
            its state; a "pending" pill was noise) and a chain-locked step's
            state is the lock line in the action zone. */}
        {step.status === 'done' ? (
          <CheckCircle2 size={16} className="shrink-0 text-emerald-500" />
        ) : step.status === 'missed' && !chainBlocked ? (
          <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-600 dark:bg-red-500/15 dark:text-red-400">
            missed
          </span>
        ) : step.status.startsWith('skipped') ? (
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-400 dark:bg-white/10 dark:text-slate-500">
            {chipLabel(step)}
          </span>
        ) : null}
      </div>
      <div className="mt-1 flex items-center justify-between text-xs">
        <span className="inline-flex items-center gap-1.5 text-slate-400 dark:text-slate-500">
          {/* The title already names the action (Call #2, SMS #1) — no verb. */}
          Between {windowLabel(step)}
          {/* Lunch credit (derived pause, 2026-08-11): the window shows AS
              AUTHORED; this chip carries the pause story — live "paused +Xm"
              during lunch (credit ticking up), then "+Xm" while the resumed
              clock runs, and it PERSISTS on done steps via the completion
              stamp (the audit answer "was this late, or lunch?" stays
              visible). */}
          {lunchPaused || lunchCreditMin > 0 ? (
            <span
              title={
                lunchPaused
                  ? `Away (${lunchPaused === 'meeting' ? 'a meeting' : lunchPaused === 'away' ? 'a break' : lunchPaused === 'call' ? 'a call' : 'lunch'}): the clock is paused and resumes after, with this much time added to the due time.`
                  : `The due time moved ${lunchCreditMin}m for lunch or a meeting.`
              }
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-sky-50 px-1.5 py-px text-[10px] font-semibold text-sky-700 ring-1 ring-sky-200 dark:!bg-sky-500/15 dark:text-sky-300 dark:ring-sky-500/30"
            >
              <AwayGlyph kind={lunchPaused || null} />
              {lunchPaused
                ? `away${lunchCreditMin > 0 ? ` +${lunchCreditMin}m` : ''}`
                : `+${lunchCreditMin}m`}
            </span>
          ) : null}
          {/* Done steps carry no window hint at all — on-time work is
              silent, and late/early already narrate on the done line. */}
          {step.status !== 'done' && step.meta?.reflowedAfter ? (
            <ReflowHint
              label={
                cadenceVersion?.definition?.steps?.find(
                  (d) => d.stepKey === step.meta.reflowedAfter,
                )?.label || 'the previous step'
              }
            />
          ) : null}
        </span>
        {/* Chain order is self-evident from the numbered titles; the locked
            state below already explains WHY a step is waiting. The lunch chip
            REPLACES the countdown (Nic 2026-08-11: the row is small and the
            time left already lives on the board card). */}
        {chainBlocked ||
        (step.status === 'pending' &&
          (lunchPaused || lunchCreditMin > 0)) ? null : actionable ? (
          <span
            className={
              tone.state === 'automated'
                ? 'font-medium text-violet-500 dark:text-violet-400'
                : tone.state === 'due'
                  ? 'font-semibold text-amber-600 dark:text-amber-400'
                  : tone.state === 'overdue'
                    ? 'font-semibold text-red-500 dark:text-red-400'
                    : 'text-slate-400 dark:text-slate-500'
            }
          >
            {step.status === 'missed'
              ? 'still worth doing'
              : stepCountdownLabel(clockStep, tone, now, workCalendar)}
          </span>
        ) : null}
      </div>
      {/* Auto-send hit an unfilled token (the bio, almost always): the sweep
          stops retrying and this is manual work again — say so loudly. */}
      {step.meta?.autoSendBlocked ? (
        <div className="mt-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
          Auto-send blocked: fill in the bio (or fix the script token), then
          send it manually.
        </div>
      ) : null}
      {/* After hours (2026-10-14): a late arrival's evening steps. */}
      {step.status === 'pending' && step.meta?.afterHours ? (
        <div className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
          After closing. Do it tonight if you can; if not, it won't count
          against you.
        </div>
      ) : null}
      {step.status === 'skipped_condition' &&
      step.meta?.reason === 'after_hours' ? (
        <div className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
          After hours. It fell after closing and wasn't done that evening, so
          it doesn't count.
        </div>
      ) : null}
      {/* A bare "skipped" chip explains nothing — say why. */}
      {step.status === 'skipped_condition' &&
      step.meta?.reason === 'not_morning_lead' ? (
        <div className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
          Morning leads only. This lead arrived after noon.
        </div>
      ) : null}
      {/* bio_unfilled = the 2026-08-25 prod repair's reason; unresolved_token
          = what the sweep stamps going forward. Same story either way. */}
      {step.status === 'skipped_condition' &&
      ['unresolved_token', 'bio_unfilled'].includes(step.meta?.reason) ? (
        <div className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
          The sender's bio (or another script token) was unfilled, so the
          automated email was skipped.
        </div>
      ) : null}
      {step.status === 'skipped_optout' ? (
        <div className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
          Lead opted out of texts (replied STOP).
        </div>
      ) : null}
      {step.status === 'skipped_no_email' ? (
        <div className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
          No email on record for this lead, so there was nothing to send.
        </div>
      ) : null}
      {step.status === 'done' && step.completedAt ? (
        <div className="mt-0.5 flex items-center gap-1.5 text-xs">
          <span className="font-medium text-emerald-600 dark:text-emerald-400">
            @ {timeLabel(step.completedAt)}
          </span>
          {step.meta?.outcome ? (
            <span className="text-slate-400 dark:text-slate-500">
              ·{' '}
              {step.meta.outcome === 'left_vm'
                ? 'left voicemail'
                : step.meta.outcome.replace('_', ' ')}
            </span>
          ) : null}
          {/* Done by someone else (server only sets completedByName then),
              e.g. a manager. */}
          {step.completedByName ? (
            <span
              title={`Done by ${step.completedByName}`}
              className="rounded-full bg-sky-50 px-1.5 py-px text-[10px] font-semibold text-sky-600 dark:bg-sky-500/15 dark:text-sky-400"
            >
              By {step.completedByName.split(/\s+/)[0]}
            </span>
          ) : null}
          {(() => {
            const story = doneStory(step);
            return story ? (
              <SlaHint message={story.message} tone={story.tone} />
            ) : null;
          })()}
          {/* Open signal from Resend tracking — a fact worth knowing, not
              proof: privacy proxies preload images (false opens) and image
              blocking hides real ones. Clicked is the stronger signal. */}
          {step.emailEngagement?.openedAt ? (
            <span
              title={`Opened ${new Date(step.emailEngagement.openedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}${step.emailEngagement.openCount > 1 ? ` (${step.emailEngagement.openCount} times)` : ''}. Open tracking is approximate: some mail apps preload images and blocked images hide real opens.`}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-px text-[10px] font-semibold text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400"
            >
              <MailOpen size={11} />
              {step.emailEngagement.clickedAt ? 'Opened · clicked' : 'Opened'}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Aborted dial attempts ("didn't go through") — quiet forensic line,
          not a ding: honest glitches happen, but 5 of them tells a story.
          Count only; the full who/when trail lives in step meta +
          call_attempt_discarded events. */}
      {(step.meta?.discardedAttempts || []).length > 0 ? (
        <div className="mt-0.5 text-[10px] font-medium text-slate-300 dark:text-slate-600">
          &quot;Didn&apos;t go through&quot; selected{' '}
          {step.meta.discardedAttempts.length === 1
            ? 'once'
            : `${step.meta.discardedAttempts.length} times`}
        </div>
      ) : null}

      {/* ZONE 4 — action bar: everything about DOING the step lives here.
          Primary action + ghost Script toggle + the VM instruction (it's an
          instruction about performing the call, not identity). Chain-locked
          steps get a quiet lock line instead of a button-shaped slab. */}
      {/* While compose is open the whole bar hides: the panel already holds
          the script text and its own "Send text now" — a second Send button
          + Script toggle above it is pure noise (Nic 2026-08-06). Cancel
          brings the bar back. */}
      {actionable && !composeOpen && !readOnly ? (
        <div className="mt-2.5 flex items-center gap-2">
          {chainBlocked ? (
            <>
              <span
                className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 dark:text-slate-500"
                title={`${dependency.label || 'The previous step'} has to happen first. This window re-times from when it actually happens`}
              >
                <Lock size={11} className="shrink-0" />
                After {dependency.label || 'the previous step'}
              </span>
              {scriptToggle}
            </>
          ) : (
            <>
              {step.type === 'call' ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onCall(step)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100"
                >
                  <Phone size={12} /> Call now
                </button>
              ) : (
                <button
                  type="button"
                  disabled={
                    busy ||
                    sending ||
                    (step.type === 'email' && !run.lead?.leadEmail)
                  }
                  onClick={() =>
                    composeOpen ? setComposeOpen(false) : openCompose()
                  }
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100"
                  title={
                    step.type === 'sms'
                      ? 'Review the text, then send it from your number'
                      : run.lead?.leadEmail
                        ? 'Review the email, then send it'
                        : 'No email on record for this lead'
                  }
                >
                  <Send size={12} />
                  Compose {step.type === 'sms' ? 'text' : 'email'}
                </button>
              )}
              {scriptToggle}
              {step.type === 'call' ? (
                step.leaveVoicemail ? (
                  <span
                    title="If there's no answer, leave the voicemail script."
                    className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-600 dark:bg-blue-500/15 dark:text-blue-400"
                  >
                    <Voicemail size={11} />
                    Leave VM
                  </span>
                ) : (
                  <span
                    title="If there's no answer, hang up. Do NOT leave a voicemail on this attempt."
                    className="ml-auto shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-400 dark:bg-white/10 dark:text-slate-500"
                  >
                    No VM
                  </span>
                )
              ) : null}
            </>
          )}
        </div>
      ) : null}
      {/* Resolved steps (done/skipped/cancelled) keep the Script toggle so
          an open script can still be closed after the call finishes. */}
      {(!actionable || readOnly) && scriptToggle ? (
        <div className="mt-2.5 flex items-center gap-2">{scriptToggle}</div>
      ) : null}

      {/* Compose panel (Phase 3 real sends): editable, prefilled from the
          script. Sending completes the step; a failed send leaves it pending
          with the server's message in a toast. */}
      {composeOpen && actionable && !chainBlocked && !readOnly ? (
        <div className="mt-2 rounded-lg bg-slate-50 p-2.5 dark:bg-white/5">
          {step.type === 'email' ? (
            <input
              value={draftSubject}
              onChange={(e) => setDraftSubject(e.target.value)}
              placeholder="Subject"
              className="mb-1.5 w-full rounded-md border-0 bg-white px-2.5 py-1.5 text-[13px] font-semibold text-slate-700 shadow-sm placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:!bg-surface-elevated dark:text-slate-200 dark:placeholder:text-slate-600"
            />
          ) : null}
          <textarea
            value={draftText}
            onChange={(e) => {
              setDraftText(e.target.value);
              autosizeTextarea(e.target);
            }}
            // Auto-grow to the full prefilled script (callback ref fires on
            // mount, when the content is already in place) — composing should
            // never mean scrolling inside a 6-row box. Manual resize still
            // allowed.
            ref={autosizeTextarea}
            rows={3}
            className="w-full resize-y rounded-md border-0 bg-white px-2.5 py-1.5 text-[13px] leading-relaxed text-slate-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300 dark:!bg-surface-elevated dark:text-slate-300"
          />
          {/* The STOP compliance line is invisible here on purpose (Nic
              2026-08-10): the prefill strips it and the server quietly
              appends it to every outgoing text — nothing to show, nothing
              to edit. */}
          <div className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
            {step.type === 'sms'
              ? 'Sends from your number.'
              : 'Sends from Veritas Ratings. Replies go to the lead owner.'}
          </div>
          {unresolvedToken ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-2.5 py-2 text-[11px] font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
              <span className="min-w-0 flex-1">
                This message still has an unfilled token. Nothing sends until
                it&apos;s filled in or removed.
                {unresolvedHint ? <> {unresolvedHint}</> : null}
              </span>
              {/\[bio\]/i.test(`${draftSubject}\n${draftText}`) && onOpenBio ? (
                <button
                  type="button"
                  onClick={onOpenBio}
                  className="shrink-0 rounded-md bg-amber-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-amber-700"
                >
                  Add my bio
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="mt-2 flex items-center justify-between">
            <button
              type="button"
              disabled={
                sending ||
                busy ||
                unresolvedToken ||
                !draftText.trim() ||
                (step.type === 'email' && !draftSubject.trim())
              }
              onClick={doSend}
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100"
            >
              <Send size={12} />
              {sending
                ? 'Sending…'
                : `Send ${step.type === 'sms' ? 'text' : 'email'} now`}
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={busy || sending}
                onClick={() => onManualComplete(step)}
                title="Already sent this outside the board? Mark the step done without sending."
                className="text-[11px] font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                Sent already? Mark done
              </button>
              <button
                type="button"
                onClick={() => setComposeOpen(false)}
                className="text-xs font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Script panel opens BELOW the action bar (its toggle lives there). */}
      {rendered && showScript ? (
        <div
          className={`relative mt-2 whitespace-pre-wrap rounded-lg bg-slate-50 p-2.5 text-[13px] leading-relaxed text-slate-600 dark:bg-white/5 dark:text-slate-300 ${
            step.type === 'call' ? '' : 'pr-9'
          }`}
        >
          {content.subject ? (
            <div className="mb-1 font-semibold text-slate-700 dark:text-slate-200">
              Subject:{' '}
              {renderScript(content.subject, {
                lead: run.lead,
                user,
                assigneeNumber: run.ownerPhone,
              })}
            </div>
          ) : null}
          {/* Which wording this lead gets (their source's, or their partner's
              own). Shown wherever the script is, so it's never a surprise. */}
          {content.variantLabel ? (
            <div className="mb-1 inline-flex items-center gap-1 rounded-full bg-sky-50 px-1.5 py-px text-[10px] font-semibold text-sky-600 dark:bg-sky-500/15 dark:text-sky-400">
              Wording: {content.variantLabel}
            </div>
          ) : null}
          {rendered}
          {/* No copy affordance (Nic 2026-08-09): texts/emails SEND from the
              board itself — copying out was a bypass around the send path
              (and its unresolved-token guard). */}
        </div>
      ) : null}
    </div>
  );
};

const VIA_OPTIONS = [
  { via: 'call', label: 'They answered a call', icon: Phone },
  { via: 'callback', label: 'They called us back', icon: PhoneIncoming },
  { via: 'sms', label: 'They texted back', icon: MessageSquare },
  { via: 'email', label: 'They replied by email', icon: Mail },
];

// ---------------------------------------------------------------------------
// Outbound reconcile: off-page RC activity (calls/texts from the queue
// number that didn't go through this page), with one-click adoption onto a
// matching pending step. Already-linked records are de-emphasized, never
// hidden. The section renders nothing when there's no activity.
// ---------------------------------------------------------------------------

const rcWhen = (d) =>
  new Date(d).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const ADOPT_OUTCOMES = [
  { value: 'reached', label: 'Reached them' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'left_vm', label: 'Left VM' },
];

// Top-level components ON PURPOSE: defined inline inside RcActivitySection
// they'd get a fresh component identity every render, and the board's 1s SLA
// tick would remount them constantly — closing the native select dropdown
// mid-choice (live bug 2026-08-03).
const RcAdoptPanel = ({
  kind,
  rcId,
  steps,
  busy,
  adopting,
  stepChoice,
  setStepChoice,
  onAdopt,
  onCancel,
}) => {
  if (steps.length === 0) {
    return (
      <div className="mt-1.5 text-[11px] text-slate-400 dark:text-slate-500">
        No open {kind === 'call' ? 'call' : 'text'} step today to adopt this
        onto.
      </div>
    );
  }
  return (
    <div className="mt-1.5 space-y-1.5">
      {steps.length > 1 ? (
        <select
          value={stepChoice || steps[0].cadenceStepId}
          onChange={(e) => setStepChoice(e.target.value)}
          className="w-full rounded-md border-0 bg-white px-2 py-1.5 text-xs text-slate-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300 dark:!bg-surface-elevated dark:text-slate-300"
        >
          {steps.map((s) => (
            <option key={s.cadenceStepId} value={s.cadenceStepId}>
              {s.label || s.stepKey} (day {s.day})
            </option>
          ))}
        </select>
      ) : null}
      {kind === 'call' ? (
        // One click adopts: choosing the outcome IS the confirmation.
        <>
          <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
            {adopting
              ? 'Adopting…'
              : `How did it go? Choosing completes ${
                  steps.length === 1
                    ? steps[0].label || steps[0].stepKey
                    : 'the step'
                }.`}
          </div>
          <div className="flex items-center gap-1.5">
            {ADOPT_OUTCOMES.map((o) => (
              <button
                key={o.value}
                type="button"
                disabled={busy || adopting}
                onClick={() => onAdopt(kind, rcId, o.value)}
                className="rounded-md bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100"
              >
                {o.label}
              </button>
            ))}
            <button
              type="button"
              onClick={onCancel}
              className="ml-1 text-[11px] font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={busy || adopting}
            onClick={() => onAdopt(kind, rcId)}
            className="rounded-md bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100"
          >
            {adopting
              ? 'Adopting…'
              : `Adopt onto ${steps.length === 1 ? steps[0].label || steps[0].stepKey : 'step'}`}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-[11px] font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

const RcActivityRow = ({
  kind,
  rcId,
  icon: Icon,
  title,
  caption,
  linkedNote,
  steps,
  busy,
  openKey,
  onToggle,
  panelProps,
  readOnly = false,
}) => {
  const key = `${kind}:${rcId}`;
  return (
    <div
      className={`rounded-lg bg-white p-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10 ${
        linkedNote ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon
          size={13}
          className="shrink-0 text-slate-400 dark:text-slate-500"
        />
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-600 dark:text-slate-300">
          {title}
        </span>
        {linkedNote ? (
          <span className="shrink-0 text-[11px] text-slate-400 dark:text-slate-500">
            {linkedNote}
          </span>
        ) : readOnly ? null : (
          <button
            type="button"
            disabled={busy}
            onClick={() => onToggle(key)}
            className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/15"
          >
            Adopt
          </button>
        )}
      </div>
      {caption ? (
        <div className="mt-0.5 pl-[21px] text-[11px] text-slate-400 dark:text-slate-500">
          {caption}
        </div>
      ) : null}
      {openKey === key ? (
        <RcAdoptPanel kind={kind} rcId={rcId} steps={steps} {...panelProps} />
      ) : null}
    </div>
  );
};

// Skeleton twin of RcActivityRow: same card, shimmering title/caption bars —
// reads as "rows are coming", not blank space.
const RcSkeletonRow = ({ wide }) => (
  <div className="rounded-lg bg-white p-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
    <div className="flex items-center gap-2">
      <div className="h-3 w-3 shrink-0 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
      <div
        className={`h-3 animate-pulse rounded bg-slate-200 dark:bg-white/10 ${wide ? 'w-36' : 'w-28'}`}
      />
    </div>
    <div
      className={`mt-1.5 ml-[21px] h-2.5 animate-pulse rounded bg-slate-100 dark:bg-white/5 ${wide ? 'w-24' : 'w-16'}`}
    />
  </div>
);

// Height-morphing wrapper: measures its content (ResizeObserver) and
// transitions the container height, so skeleton -> rows / empty-line never
// snaps the layout — everything below glides. Content changes inside (the
// adopt panel opening, rows appearing) ride the same animation.
const RcBodyReveal = ({ children }) => {
  const innerRef = useRef(null);
  const [height, setHeight] = useState(null);
  useEffect(() => {
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setHeight(el.offsetHeight));
    ro.observe(el);
    setHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);
  return (
    <div
      className="overflow-hidden"
      style={{
        height: height == null ? 'auto' : height,
        transition: 'height 280ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
};

const RcActivitySection = ({ run, busy, readOnly = false }) => {
  const queryClient = useQueryClient();
  const activity = useFetchRunRcActivity(
    run?.state === 'active' ? run.cadenceRunId : null,
  );
  const adopt = useAdoptOutbound(null, {
    onError: (err) =>
      toast.error(err?.response?.data?.msg || 'That didn’t go through.'),
  });
  // Which record's adopt panel is open: 'call:S1' / 'sms:M1'.
  const [openKey, setOpenKey] = useState(null);
  const [stepChoice, setStepChoice] = useState('');

  const data = activity.data;
  // The server already excludes system-originated records (board sends,
  // page-dialed calls). What arrives here is genuinely-outside activity:
  // adoptable records, fuzzy "likely logged" calls, and PRIOR ADOPTIONS —
  // which stay listed (marked "adopted") as the trace of how that outside
  // touch got reconciled (Nic, 2026-08-03).
  const calls = data?.calls || [];
  const texts = data?.texts || [];
  const hasContent = !!data && calls.length + texts.length > 0;

  // The section is ALWAYS present for an active run (Nic, 2026-08-03) — the
  // RC lookup takes a second or two (live RingCentral round-trip), so a
  // sudden appearance shoved everything below it down. Persistent section +
  // skeleton rows while loading = zero layout jump; empty and RC-down get
  // honest one-liners instead of silence.
  if (!run || run.state !== 'active') return null;
  const loading = !data;
  const rcUnavailable = !!data?.rcUnavailable;

  const adoptable = (type) =>
    (data.pendingAdoptableSteps || []).filter((s) => s.type === type);

  // `outcome`: the button the rep clicked (QA 2026-10-26: it was always
  // sent as reached, so every adopted call told partners we reached them).
  const doAdopt = async (kind, rcId, outcome) => {
    const steps = adoptable(kind);
    const cadenceStepId =
      stepChoice || (steps.length === 1 ? steps[0].cadenceStepId : '');
    if (!cadenceStepId) return;
    if (kind === 'call' && !ADOPT_OUTCOMES.some((o) => o.value === outcome)) {
      return;
    }
    try {
      await adopt.mutateAsync({
        cadenceStepId,
        kind,
        ...(kind === 'call'
          ? { rcSessionId: rcId, outcome }
          : { rcMessageId: rcId }),
      });
      toast.success(kind === 'call' ? 'Call adopted.' : 'Text adopted.');
      setOpenKey(null);
      queryClient.invalidateQueries(['onboarding-board']);
      // Adopting consumes an RC record: refresh the outside-activity list
      // (keyed outside the board prefix so the stream can't hammer RC).
      queryClient.invalidateQueries(['rc-activity']);
    } catch (err) {
      /* toasted above */
    }
  };

  const panelProps = {
    busy,
    adopting: adopt.isLoading,
    stepChoice,
    setStepChoice,
    onAdopt: doAdopt,
    onCancel: () => setOpenKey(null),
  };

  const rowProps = {
    busy,
    readOnly,
    openKey,
    onToggle: (key) => {
      setStepChoice('');
      setOpenKey(openKey === key ? null : key);
    },
  };

  return (
    <Section>
      {/* Custom header row: the refresh button lives where the Section title
          would be. The list is a live RC lookup cached for 30s — refresh
          bypasses the cache so a just-made call shows without closing and
          reopening the panel (Nic, 2026-08-05). */}
      {/* Matches the Section rule, with the refresh button riding inside the
          centered group so the affordance stays next to its label. */}
      <div className="mb-3 flex items-center gap-3">
        <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
        <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
          Outside activity
        </span>
        <button
          type="button"
          onClick={() => activity.refetch()}
          disabled={activity.isFetching}
          title="Check RingCentral again now"
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50 dark:hover:bg-white/10 dark:hover:text-slate-300"
        >
          <RefreshCw
            size={13}
            className={activity.isFetching ? 'animate-spin' : ''}
          />
        </button>
        <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
      </div>
      <p className="mb-2 text-[11px] leading-snug text-slate-400 dark:text-slate-500">
        Calls and texts to this lead made directly from RingCentral, not through
        this page.{' '}
        {readOnly
          ? 'Only the lead owner can adopt one onto a step.'
          : 'Adopt one to complete its step using the real time it happened.'}
      </p>
      <RcBodyReveal>
        {loading ? (
          <div className="space-y-1.5">
            <RcSkeletonRow wide />
            <RcSkeletonRow />
          </div>
        ) : rcUnavailable ? (
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Couldn't reach RingCentral just now. Hit refresh above to retry.
          </p>
        ) : !hasContent ? (
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Nothing outside the board yet for this lead.
          </p>
        ) : null}
        {hasContent ? (
          <div className="space-y-1.5">
            {calls.map((c) => (
              <RcActivityRow
                key={c.rcSessionId}
                kind="call"
                rcId={c.rcSessionId}
                icon={Phone}
                title={`Call · ${rcWhen(c.startTime)}`}
                caption={[
                  c.durationSeconds
                    ? shortDuration(c.durationSeconds * 1000)
                    : null,
                  c.result || null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                linkedNote={
                  c.adoptedByStepId
                    ? 'adopted ✓'
                    : c.probablyLogged
                      ? 'likely logged'
                      : null
                }
                steps={adoptable('call')}
                panelProps={panelProps}
                {...rowProps}
              />
            ))}
            {texts.map((t) => (
              <RcActivityRow
                key={t.rcMessageId}
                kind="sms"
                rcId={t.rcMessageId}
                icon={MessageSquare}
                title={`Text · ${rcWhen(t.creationTime)}`}
                caption={t.text}
                linkedNote={t.adoptedByStepId ? 'adopted ✓' : null}
                steps={adoptable('sms')}
                panelProps={panelProps}
                {...rowProps}
              />
            ))}
          </div>
        ) : null}
      </RcBodyReveal>
    </Section>
  );
};

// Calendly booking display state: 'active' splits on START time (upcoming
// vs presumed happened) — the same boundary the metrics page counts by
// (2026-08-18), so the two surfaces never disagree about a live meeting.
// Canceled comes from the webhook; no-show lands via the daily reconcile
// sweep (Calendly isn't delivering no-show webhooks).
const bookingDisplayStatus = (booking, now) => {
  if (!booking) return null;
  if (booking.status === 'canceled') return 'Canceled';
  if (booking.status === 'no_show') return 'No-show';
  return new Date(booking.startTime) <= (now ? new Date(now) : new Date())
    ? 'Happened'
    : 'Upcoming';
};

const bookingTimeLabel = (booking) => {
  const start = new Date(booking.startTime);
  const datePart = start.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const timePart = start.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${datePart} at ${timePart}`;
};

const BOOKING_HEADLINE = {
  Upcoming: 'Meeting scheduled',
  Happened: 'Meeting happened',
  Canceled: 'Meeting canceled',
  'No-show': 'Meeting missed (no-show)',
};

// Header banner for a detected Calendly booking — the RC detection callout's
// sibling: the system spotted it, the human decides the move. Two lines,
// NOTHING truncated (Nic 2026-08-10): the when is the headline, the
// what/who sits below it.
const BookingBanner = ({ booking, now }) => {
  const display = bookingDisplayStatus(booking, now);
  const live = display === 'Upcoming' || display === 'Happened';
  return (
    <div
      className={`mt-2 rounded-lg px-2.5 py-2 text-xs font-medium ${
        live
          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
          : 'bg-amber-50/70 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400'
      }`}
    >
      <div className="flex items-start gap-1.5">
        <CalendarCheck size={15} className="mt-px shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold">
            {BOOKING_HEADLINE[display] || 'Meeting'}:{' '}
            {bookingTimeLabel(booking)}
          </div>
          <div
            className={`mt-0.5 ${
              live
                ? 'text-emerald-600/90 dark:text-emerald-400/80'
                : 'text-amber-600/90 dark:text-amber-400/80'
            }`}
          >
            {booking.eventTypeName || 'Meeting'}
            {booking.hostName ? ` with ${booking.hostName}` : ''}
          </div>
        </div>
        <span
          className={`shrink-0 rounded px-1.5 py-px text-[10px] font-bold uppercase tracking-wide ${
            live
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
              : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
          }`}
        >
          {display}
        </span>
      </div>
    </div>
  );
};

const LeadSlideOver = ({
  open,
  onClose,
  intakeLead, // pre-take mode
  run, // post-take mode
  now: liveNow,
  cadenceVersion,
  scripts, // LIVE scripts store (board payload) — step previews/prefills
  ownerLunch = null, // the run OWNER's effective lunch today (derived pause)
  user,
  onTake,
  onCall,
  onFollowUpCall = null, // Contact Made "Call them now" (command center)
  boardContext = {}, // call workspace context (signup opens the workspace)
  onTextNudge = null, // Text them (NudgeComposer)
  onOutsideCadenceCall = null, // day-column ad-hoc call, counts toward nothing
  onManualComplete,
  onSendSms,
  onSendEmail,
  onContactMade,
  onConfirmDetection,
  onDismissDetections,
  onOutcome,
  onDrop, // (reason) => drop the lead: ends the run, leaves the board
  onDismissStop = null, // "Keep working" on a STOP-parked run
  onStartNow = null, // (run) => start a waiting new lead early
  onOpenBio,
  busy,
  callDocked = false, // dialer is a bottom bar (<1100px): pad the scroll body
  // Manager reassignment (all three present only for manage holders).
  reassignSpecialists = null,
  onReassignRun = null,
  onReassignIntake = null,
  // Reversible Push (Nic 2026-08-15): only offered during the last working
  // hour, mirroring the server's window guard.
  onPushLead = null,
  onUnpushLead = null,
}) => {
  const [viaPicker, setViaPicker] = useState(false);
  // The signup flyover (Nic 2026-09-22): start or resume the lead's signup
  // from here too, with or without a live call.
  const { openSignup } = useCallCenter();
  // Book, Nurture and Drop live in the Call Desk (Nic 2026-09-30).
  // STOP card two-tap (Nic 2026-08-18: a "Confirm drop" that only prefilled
  // the dropdown below read as a dead button). First tap arms, second drops
  // with the canned reason. NO timed auto-disarm: the Cancel button is the
  // only way back (a timer racing the user's Cancel click could swap the
  // button to Keep working right under their cursor). Resets on lead change.
  const [stopDropArmed, setStopDropArmed] = useState(false);
  useEffect(() => {
    setStopDropArmed(false);
  }, [run?.cadenceRunId]);
  const queryClient = useQueryClient();
  // Inline email add/edit (Nic 2026-08-20): the specialist owns the booking,
  // and a phone-only lead who agrees to a Calendly link on the call has no
  // address to send it to. Lead Handoffs got this editor on 8/17; the board
  // is where the cadence actually runs, so it needs it too.
  const saveEmailMut = useSaveLeadEmail(() => {
    queryClient.invalidateQueries(['onboarding-board']);
  });
  const workCalendar = useWorkCalendar();
  // The viewer's saved bio, merged into the render user so script previews
  // and compose prefills show the REAL bio once it's filled (the auth user
  // object doesn't carry it).
  const profileQ = useFetchCallProfile({ enabled: open });
  const senderUser = {
    ...user,
    userCadenceBio:
      profileQ.data?.profile?.userCadenceBio || user?.userCadenceBio || null,
  };
  useEffect(() => {
    if (!open) {
      setViaPicker(false);
    }
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const lead = run?.lead || intakeLead;
  if (!lead) return null;

  // Ownership rule (Nic 2026-09-29): only the lead's owner acts on it.
  // Managers can read everything here and reassign (the card above), nothing
  // else. A communal intake lead (no assignee) is anyone's to Take or Push,
  // and that is all until someone owns it.
  const viewerId = boardContext?.viewerUserId || user?.userId || null;
  const communal = !run && !lead.intakeAssignedTo;
  const isMine = run
    ? isOwnedBy(run.ownerUserId, viewerId)
    : !communal &&
      (isOwnedBy(lead.intakeAssignedTo, viewerId) || !!lead.isMine);
  const ownerName = run ? run.ownerName : lead.intakeAssignedToName;

  // Call in progress: freeze the panel's clock at the dial instant, exactly
  // like the tile does (Nic 2026-08-19: a checklist countdown could tick red
  // in here while the same lead sat paused yellow on the board).
  // Awaiting log (Nic 2026-08-24): same freeze for the wrap-up gap — the
  // call hung up in-window (effective end, matching every other judgment)
  // and the outcome isn't clicked yet. Without this the checklist row here
  // burned red while the tile behind it held amber "Log the call".
  const awaitingLogStep =
    !run?.activeCall && run
      ? (run.steps || []).find((s) => {
          if (s.status !== 'pending' || !s.meta?.callPlacedAt) return false;
          const placed = new Date(s.meta.callPlacedAt).getTime();
          if (Number.isNaN(placed)) return false;
          return (
            placed <=
              new Date(
                effectiveStepWindow(s, ownerLunch, liveNow).endAt,
              ).getTime() && liveNow - placed < AWAITING_LOG_TTL_MS
          );
        })
      : null;
  const now = run?.activeCall
    ? new Date(run.activeCall.startedAt)
    : awaitingLogStep
      ? new Date(awaitingLogStep.meta.callPlacedAt)
      : liveNow;

  const isContact = run?.state === 'contact_made';
  // Clock start (migration 172): in the future for a lead that arrived
  // outside working hours or was pushed. Take is refused until then, so the
  // panel must not offer it.
  const startsAt = intakeStartOf(lead);
  // When this lead actually becomes actionable. With a lunch credit or an
  // after-hours ladder slot this sits LATER than the hard clock start, and
  // it is the moment the specialist cares about.
  const releaseAt = intakeReleaseOf(lead);
  const notStarted = intakeNotStarted(lead, now);
  // Pushed = a future clock start that came from Push, not an after-hours
  // arrival: the copy differs and only a pushed lead offers Undo.
  const pushed = !!lead.leadPushed && notStarted;
  // A new lead's run that hasn't started yet (Nic 2026-10-25): locked like
  // the old Intake. After hours it just says when it starts; while the board
  // is open its owner may Start now. Contact made stays only when they
  // reached out first (a callback or a text back).
  const runStartsAt =
    run && !isContact && run.startsAt && new Date(run.startsAt) > liveNow
      ? run.startsAt
      : null;
  const theyReachedOut =
    (run?.reachOuts || []).length > 0 || !!run?.detections?.items?.length;
  const pushWindowOpen = inPushWindow(workCalendar, now);
  const todaysSteps = run ? stepsForToday(run) : [];
  // The lead's detected Calendly booking (runs and intake leads both carry
  // one). "Confirmable" = still active on Calendly's side.
  const booking = run ? run.booking : intakeLead?.booking || lead.booking;
  // A booking already in Meeting booked has nothing left to confirm
  // (2026-09-23: bookings attach on their own and stay with the rep).

  // The reached-out banner (Nic 2026-09-30): Signing Up shows it inside
  // What's needed instead, so its header is only who the lead is.
  const detectionBanner =
    run?.detections &&
    (run.detections.calls > 0 || run.detections.texts > 0) ? (
      <div className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 text-xs font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
        <div className="flex items-center gap-1.5">
          <Handshake size={15} className="shrink-0" />
          <span>
            {lead.leadFirstName} reached out:{' '}
            {run.detections.calls > 0
              ? `${run.detections.calls} call(s)`
              : ''}
            {run.detections.calls > 0 && run.detections.texts > 0
              ? ' and '
              : ''}
            {run.detections.texts > 0
              ? `${run.detections.texts} text(s)`
              : ''}{' '}
            from this number.
          </span>
          {isMine ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onDismissDetections(lead.leadId)}
              className="ml-auto shrink-0 text-[11px] font-medium text-amber-600/80 hover:text-amber-800 dark:text-amber-500 dark:hover:text-amber-300"
            >
              Dismiss
            </button>
          ) : null}
        </div>
      </div>
    ) : null;
  // The reference info gets its own labeled zone, through the same Section
  // header as My notes (collapsible on Signing Up).
  const leadDetails = (collapsible) => (
    <Section title="Lead details" collapsible={collapsible}>
      <LeadDetailSections
        lead={lead}
        onSaveEmail={
          isMine
            ? (email) =>
                saveEmailMut.mutateAsync({
                  leadId: lead.leadId,
                  email,
                })
            : null
        }
      />
    </Section>
  );

  return createPortal(
    <div className="fixed inset-0 z-[1060]">
      <div
        className="absolute inset-0 bg-slate-900/25 dark:bg-black/50"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute bottom-0 right-0 top-0 flex w-full max-w-md flex-col bg-[#F5F7FA] shadow-2xl dark:bg-surface-page">
        {/* Header */}
        <div className="bg-white px-4 py-3 shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:!bg-surface-elevated">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5 text-base font-bold text-slate-800 dark:text-slate-100">
                <span className="truncate">{fullLeadName(lead)}</span>
                {lead.existingAccount ? (
                  <ExistingAccountMark account={lead.existingAccount} />
                ) : null}
              </div>
              {/* Two calm rows, never a chip pile-up: identity (the lead's
                  phone — the SOURCE chip moved out of this row entirely: a
                  referrer's name next to a number read as THEIR number, and
                  source already lives in the Lead details Source card), then
                  status (stage chip + opt-out + the reached-out fact). */}
              <div className="mt-0.5 flex min-w-0 items-center gap-2 whitespace-nowrap text-xs text-slate-400 dark:text-slate-500">
                <span className="shrink-0">{formatPhone(lead.leadPhone)}</span>
                {/* Email up top too (Nic 2026-08-09). A missing address is a
                    fact worth stating (Nic 2026-08-17): the automated Day-2
                    email skips on it, so say so instead of hiding the slot. */}
                <span className="shrink-0">·</span>
                {lead.leadEmail ? (
                  <span className="truncate" title={lead.leadEmail}>
                    {lead.leadEmail}
                  </span>
                ) : (
                  <span className="shrink-0 italic">No email on record</span>
                )}
              </div>
              {/* Signing Up keeps the header to who they are (Nic
                  2026-09-30): the column, the need and the time left live
                  in What's needed below. */}
              {!isContact || lead.leadSmsOptOutAt ? (
                <div className="mt-1.5 flex min-w-0 items-center gap-2 whitespace-nowrap text-xs">
                  {run ? (
                    isContact ? null : (
                      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-500 dark:bg-white/10 dark:text-slate-400">
                        {`Day ${Math.min(run.currentDay, 10)} of 10`}
                      </span>
                    )
                  ) : (
                    <span className="shrink-0 rounded-full bg-indigo-50 text-indigo-700 dark:!bg-indigo-500/15 dark:text-indigo-300 px-2 py-0.5 font-semibold">
                      New lead
                    </span>
                  )}
                  {/* Mirror of the tile's BellOff badge — the open panel must
                      say what the tile says. */}
                  {lead.leadSmsOptOutAt ? (
                    <span
                      title={`SMS suppressed since ${new Date(lead.leadSmsOptOutAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-500 dark:bg-white/10 dark:text-slate-400"
                    >
                      <BellOff size={11} strokeWidth={2.6} />
                      Lead Texted STOP
                    </span>
                  ) : null}
                  {/* Reach-out history moved to ReachOutsCard in the body
                      (header line read like a log of OUR outreach). */}
                </div>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {/* Outside-of-cadence call (Nic 2026-08-13): taken leads in the
                  day columns only — an inbound email/text/missed call earns
                  an immediate dial that counts toward NO checklist step.
                  Intake has no run; Contact Made has "Call them now". */}
              {/* Not on a locked run (QA 2026-10-26): Start now first. */}
              {run &&
              isMine &&
              !isContact &&
              !runStartsAt &&
              onOutsideCadenceCall &&
              lead.leadPhone ? (
                <button
                  type="button"
                  onClick={() => onOutsideCadenceCall(run)}
                  title="Call outside the cadence. Counts toward nothing"
                  className="rounded-md p-1.5 text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-500/10"
                >
                  <PhoneOutgoing size={17} />
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          {!isContact ? (
            <>
              {detectionBanner}
              {booking ? <BookingBanner booking={booking} now={now} /> : null}
            </>
          ) : null}
        </div>

        {/* Body */}
        <div
          className={`os-scroll flex-1 overflow-y-auto px-4 py-4 ${
            callDocked ? 'pb-24' : ''
          }`}
        >
          {!run ? (
            <>
              {reassignSpecialists && onReassignIntake ? (
                <ReassignCard
                  currentOwnerId={lead.intakeAssignedTo}
                  currentOwnerName={lead.intakeAssignedToName}
                  specialists={reassignSpecialists}
                  busy={busy}
                  onReassign={(toUserId) =>
                    onReassignIntake(lead.leadId, toUserId)
                  }
                />
              ) : null}
              <Notepad lead={lead} user={user} readOnly={!isMine} />
              <LeadDetailSections lead={lead} />
              {!communal && !isMine ? (
                <NotYoursNote ownerName={ownerName} />
              ) : (
                <>
                  {/* A lead whose clock has not started cannot be taken (Nic
                      2026-08-15) — it arrived outside working hours, or someone
                      pushed it. The server 409s either way; leaving the button
                      live just meant an error in the console. */}
                  <button
                    type="button"
                    disabled={busy || notStarted}
                    onClick={() => onTake(lead)}
                    title={
                      notStarted
                        ? `This lead can't be started until ${startsAtText(startsAt, now)}`
                        : undefined
                    }
                    className={`w-full rounded-xl py-3 text-sm font-bold ${
                      notStarted
                        ? 'cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-white/10 dark:text-slate-500'
                        : 'bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100'
                    }`}
                  >
                    {notStarted
                      ? `Starts ${startsAtText(releaseAt, now)}`
                      : busy
                        ? 'Taking…'
                        : 'Take this lead'}
                  </button>
                  <p className="mt-2 text-center text-xs text-slate-400 dark:text-slate-500">
                    {notStarted ? (
                      pushed ? (
                        <>
                          This lead was pushed to the next working day, so its
                          10-day clock starts {startsAtText(releaseAt, now)}.
                          Nothing is counted against anyone until then.
                        </>
                      ) : (
                        <>
                          This lead arrived outside working hours, so its 10-day
                          clock starts {startsAtText(releaseAt, now)}. Nothing
                          is counted against anyone until then.
                        </>
                      )
                    ) : (
                      <>
                        The 10-day clock started when this lead arrived. Call #1
                        is due within 3 minutes of arriving, and windows that
                        pass before they're taken count as missed.
                      </>
                    )}
                  </p>
                  {/* Reversible Push (Nic 2026-08-15): offered only during the
                      last working hour (the server 409s outside it). Push moves
                      the clock to the next working open and re-assigns from that
                      day's roster; Undo gives it back to whoever pushed it with
                      its clock starting now (QA 2026-10-26). */}
                  {pushWindowOpen && !notStarted && onPushLead ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onPushLead(lead.leadId)}
                      className="mt-2 w-full rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-white/10 dark:!bg-surface-card dark:text-slate-300 dark:hover:bg-white/5"
                    >
                      Push to the next working day
                    </button>
                  ) : null}
                  {pushWindowOpen && pushed && onUnpushLead ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onUnpushLead(lead.leadId)}
                      className="mt-2 w-full rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-white/10 dark:!bg-surface-card dark:text-slate-300 dark:hover:bg-white/5"
                    >
                      Undo the push, work it today
                    </button>
                  ) : null}
                </>
              )}
            </>
          ) : (
            <>
              {reassignSpecialists && onReassignRun ? (
                <ReassignCard
                  currentOwnerId={run.ownerUserId}
                  currentOwnerName={run.ownerName}
                  specialists={reassignSpecialists}
                  busy={busy}
                  onReassign={(toUserId) =>
                    onReassignRun(run.cadenceRunId, toUserId)
                  }
                />
              ) : null}
              {/* Signing Up keeps their reach-outs with the rest of the
                  history, in What happened at the bottom (Nic 2026-09-30). */}
              {!isContact ? (
                <ReachOutsCard lead={lead} reachOuts={run.reachOuts} />
              ) : null}
              {isContact ? (
                // What's needed (Nic 2026-09-30): the one next thing, then
                // the ways to do it. Nothing else. The only section that
                // never folds.
                <Section title="What's needed">
                  <div className="space-y-3">
                    {detectionBanner ? (
                      <div className="[&>div]:mt-0">{detectionBanner}</div>
                    ) : null}
                    {/* STOP-park (Nic 2026-08-18): the lead texted STOP
                        before anyone spoke to them, the strongest "not
                        interested" signal the board gets. COLORLESS on
                        purpose — this is a decision, not an alarm. Confirm
                        drop is a direct TWO-TAP (arm, then drop with the
                        canned 'Texted STOP' reason); Keep working clears
                        the chip and the lead stays here. */}
                    {run.stopParkedAt ? (
                      <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-white/15 dark:!bg-surface-card">
                        <div className="mb-0.5 flex items-center gap-1.5 text-sm font-bold text-slate-700 dark:text-slate-200">
                          <BellOff size={15} /> «STOP» received
                        </div>
                        <div className="mb-2.5 text-xs text-slate-500 dark:text-slate-400">
                          {lead.leadFirstName} texted STOP before anyone spoke
                          to them. Texts are suppressed either way. Drop them,
                          or keep working them by call and email.
                        </div>
                        {isMine ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => {
                                if (!stopDropArmed) {
                                  setStopDropArmed(true);
                                  return;
                                }
                                setStopDropArmed(false);
                                onDrop('Texted STOP');
                              }}
                              className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-white disabled:opacity-50 ${
                                stopDropArmed
                                  ? 'bg-red-500 hover:bg-red-400'
                                  : 'bg-slate-900 hover:bg-slate-700 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100'
                              }`}
                            >
                              <Trash2 size={13} />
                              {stopDropArmed ? 'You sure?' : 'Confirm drop'}
                            </button>
                            {onDismissStop ? (
                              // While the drop is armed this becomes a plain
                              // Cancel that ONLY disarms — otherwise backing
                              // out of the drop would accidentally commit the
                              // opposite decision (Nic 2026-08-18).
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() =>
                                  stopDropArmed
                                    ? setStopDropArmed(false)
                                    : onDismissStop(run)
                                }
                                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200 disabled:opacity-50 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/15"
                              >
                                {stopDropArmed ? 'Cancel' : 'Keep working'}
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    <NeedStatement run={run} now={now} />

                    {!isMine ? (
                      <NotYoursNote ownerName={ownerName} />
                    ) : (
                      <div>
                        <div className="rounded-xl bg-white py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
                          {/* One door (Nic 2026-09-30): the Call Desk is where
                              reps call and sign people up, so Call now and
                              Sign up became one button. It opens on the talk
                              track, or on the signup once one is under way. */}
                          {/* Text, then the Call Desk (Nic 2026-09-30): Book,
                              Nurture and Drop live in the Call Desk's "Client
                              not ready?" bar, so the slide-over keeps just the
                              two ways to reach them. */}
                          {(() => {
                            const resume = [
                              'signup_link_sent',
                              'account_made',
                              'agreement_out',
                            ].includes(run.pipelineStage);
                            return (
                              <div className="flex items-stretch gap-2 px-3">
                                <button
                                  type="button"
                                  disabled={busy || !onTextNudge || !lead.leadPhone}
                                  onClick={() => onTextNudge(run)}
                                  title="Text them from your own line"
                                  className="flex w-16 shrink-0 flex-col items-center justify-center gap-1 rounded-xl bg-sky-600 text-xs font-semibold text-white shadow-sm outline-none transition-[background-color] hover:bg-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:opacity-50 dark:!bg-sky-500 dark:hover:!bg-sky-400"
                                >
                                  <MessageSquare size={18} />
                                  Text
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() =>
                                    resume || !lead.leadPhone || !onFollowUpCall
                                      ? openSignup({
                                          run,
                                          boardContext,
                                          leadId: run.lead.leadId,
                                          leadName: fullLeadName(run.lead),
                                        })
                                      : onFollowUpCall(run)
                                  }
                                  className="group flex min-w-0 flex-1 items-center gap-3 rounded-xl bg-slate-900 px-3 py-2.5 text-left text-white shadow-sm outline-none transition-[background-color] hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:opacity-50 dark:!bg-slate-100 dark:!text-slate-900 dark:hover:!bg-white"
                                >
                                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 dark:!bg-slate-900/10">
                                    <Headset size={18} />
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-bold">
                                      Open workspace
                                    </span>
                                    <span className="block truncate text-xs text-white/70 dark:!text-slate-600">
                                      {resume
                                        ? 'Pick up their signup where it left off'
                                        : 'Call them and sign them up'}
                                    </span>
                                  </span>
                                  <ChevronRight
                                    size={16}
                                    className="shrink-0 opacity-60 transition-transform group-hover:translate-x-0.5"
                                  />
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </Section>
              ) : (
                <Section>
                  {!isMine ? (
                    <NotYoursNote ownerName={ownerName} />
                  ) : viaPicker ? (
                    <div className="rounded-xl bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
                      <div className="mb-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        How did contact happen?
                      </div>
                      <p className="mb-2.5 text-xs leading-snug text-slate-500 dark:text-slate-400">
                        This stops the outreach cadence and moves{' '}
                        {lead.leadFirstName} to Contact Made.
                      </p>
                      <div className="space-y-1.5">
                        {(() => {
                          // Recommend the option matching how the lead has
                          // reached out (Nic 2026-08-06): any inbound CALL on
                          // record recommends "They called us back"; texts
                          // only recommends "They texted back". Recommended
                          // option floats to the top with a chip.
                          const touches = run.reachOuts || [];
                          const recommendedVia = touches.some(
                            (r) => r.type === 'call',
                          )
                            ? 'callback'
                            : touches.some((r) => r.type !== 'call')
                              ? 'sms'
                              : null;
                          const ordered = recommendedVia
                            ? [
                                ...VIA_OPTIONS.filter(
                                  (o) => o.via === recommendedVia,
                                ),
                                ...VIA_OPTIONS.filter(
                                  (o) => o.via !== recommendedVia,
                                ),
                              ]
                            : VIA_OPTIONS;
                          return ordered.map(({ via, label, icon: Icon }) => {
                            const recommended = via === recommendedVia;
                            return (
                              <button
                                key={via}
                                type="button"
                                disabled={busy}
                                onClick={() => {
                                  setViaPicker(false);
                                  // A detected inbound matching the chosen
                                  // via already carries the logged touch —
                                  // confirm THAT (links it + back-stamps to
                                  // its RC time) instead of logging a
                                  // duplicate.
                                  const item = (
                                    run.detections?.items || []
                                  ).find(
                                    (i) =>
                                      i.leadOutreachId &&
                                      (i.eventType === 'cadence_call_detected'
                                        ? 'callback'
                                        : 'sms') === via,
                                  );
                                  if (item) {
                                    onConfirmDetection(
                                      via,
                                      item.leadOutreachId,
                                    );
                                  } else {
                                    onContactMade(via);
                                  }
                                }}
                                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold disabled:opacity-50 ${
                                  recommended
                                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-300 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/40 dark:hover:bg-emerald-500/20'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10'
                                }`}
                              >
                                <Icon
                                  size={14}
                                  className={
                                    recommended
                                      ? 'text-emerald-600 dark:text-emerald-400'
                                      : 'text-slate-500 dark:text-slate-400'
                                  }
                                />
                                {label}
                                {recommended ? (
                                  <span className="ml-auto rounded bg-emerald-100 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                                    Recommended
                                  </span>
                                ) : null}
                              </button>
                            );
                          });
                        })()}
                      </div>
                      <div className="mt-2.5 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setViaPicker(false)}
                          className="text-xs font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {runStartsAt ? (
                        <NotStartedCard
                          startsAt={runStartsAt}
                          now={liveNow}
                          canStartNow={!!run.canStartNow && !!onStartNow}
                          busy={busy}
                          onStartNow={() => onStartNow(run)}
                        />
                      ) : null}
                      {runStartsAt && !theyReachedOut ? null : (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            // An observed contact ("Not yet" after a reached
                            // call) skips the via picker — it WAS a call; the
                            // server links the logged touch and backdates
                            // contactMadeAt to the observation time.
                            run.contactObservedAt
                              ? onContactMade('call')
                              : setViaPicker(true)
                          }
                          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                        >
                          <CheckCircle2 size={15} />{' '}
                          {run.contactObservedAt ||
                          (run.reachOuts || []).length > 0
                            ? 'Confirm Contact Made'
                            : 'Contact made'}
                        </button>
                        {/* No manual nurture before contact (Nic 2026-08-14):
                            a lead we have never spoken to is not ours to judge.
                            Either we reach them, or day 11 auto-nurtures. */}
                      </div>
                      )}
                      {run.contactObservedAt ? (
                        <p className="text-center text-[11px] text-slate-400 dark:text-slate-500">
                          Contact marked{' '}
                          {new Date(run.contactObservedAt).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}{' '}
                          on a call. Confirming keeps that time.
                        </p>
                      ) : null}
                    </div>
                  )}
                </Section>
              )}

              {!isContact ? (
                <>
                  <Section
                    title={`Today's checklist · day ${Math.min(run.currentDay, 10)}`}
                  >
                    {todaysSteps.length === 0 ? (
                      <div className="rounded-lg bg-white px-3 py-4 text-center text-sm text-slate-400 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:text-slate-500 dark:ring-1 dark:ring-white/10">
                        Nothing scheduled today. Give the veteran a breather.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(() => {
                          // "You are here": the first unlocked, unfinished
                          // step gets a quiet tone-matched outline.
                          const current = todaysSteps.find(
                            (s) =>
                              (s.status === 'pending' ||
                                s.status === 'missed') &&
                              !chainBlockerOf(s, run, cadenceVersion),
                          );
                          return todaysSteps.map((step) => (
                            <StepRow
                              key={step.cadenceStepId}
                              step={step}
                              run={run}
                              now={now}
                              cadenceVersion={cadenceVersion}
                              scripts={scripts}
                              ownerLunch={ownerLunch}
                              user={senderUser}
                              onCall={onCall}
                              onManualComplete={onManualComplete}
                              onSendSms={onSendSms}
                              onSendEmail={onSendEmail}
                              onOpenBio={onOpenBio}
                              busy={busy}
                              readOnly={!isMine || !!runStartsAt}
                              isCurrent={
                                current?.cadenceStepId === step.cadenceStepId
                              }
                            />
                          ));
                        })()}
                      </div>
                    )}
                  </Section>
                  {/* Off-page RC activity + adoption (renders nothing when
                      there is none). */}
                  <RcActivitySection
                    run={run}
                    busy={busy}
                    readOnly={!isMine || !!runStartsAt}
                  />
                  {/* The specialist's own zone sits between the work above
                      and the reference info below. */}
                  <Notepad lead={lead} user={user} readOnly={!isMine} />
                </>
              ) : (
                <>
                  {/* Signing Up (Nic 2026-09-30): What's needed, then My
                      notes and Lead details open, then the history (What
                      happened, Call transcripts) collapsed at the bottom.
                      Every section but What's needed folds. */}
                  <Notepad
                    lead={lead}
                    user={user}
                    readOnly={!isMine}
                    collapsible
                  />
                  {leadDetails(true)}
                  <Section title="What happened" collapsible defaultOpen={false}>
                    <ReachOutsCard lead={lead} reachOuts={run.reachOuts} />
                    <JourneyTimeline leadId={lead.leadId} bare />
                  </Section>
                  <Section
                    title="Call transcripts"
                    collapsible
                    defaultOpen={false}
                  >
                    <LeadTranscriptsCard
                      leadId={lead.leadId}
                      endpointBase="/onboarding-board/leads"
                      bare
                    />
                  </Section>
                </>
              )}

              {!isContact ? (
                <>
                  {/* Call transcripts (2026-08-18): RingSense recordings
                      filed against the lead — no more copy-pasting from
                      RingCentral. */}
                  <LeadTranscriptsCard
                    leadId={lead.leadId}
                    endpointBase="/onboarding-board/leads"
                  />
                  {leadDetails(false)}
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LeadSlideOver;
